import { http } from './http';
import dayjs from '../lib/dates';
import { NEXT_EVENT_WEEKS, earliest, nextAccompaniment, nextLesson, type NextEvent } from '../lib/nextEvent';
import {
  Bilan,
  CandidateSearchResult,
  CreateBilanPayload,
  AbsenceKind,
  DeclareAbsenceResult,
  InstructorAbsence,
  InstructorInvoice,
  MyInvoices,
  ExamForecastsResponse,
  InstructorLesson,
  LearningBooklet,
  LearningProgression,
  StartingEvaluation,
  PlanningResponse,
  Student,
  StudentDetail,
  StudentLesson,
  TodayResponse,
} from '../types';

/** Réglages de l'application des moniteurs choisis par l'auto-école (J39). */
export interface InstructorAppSettings {
  /** Note de la séance : proposée dans le résumé de séance, montrée à l'élève. */
  sessionRating: { enabled: boolean; shared: boolean };
  /** Le moniteur ouvre et retire ses créneaux à réserver depuis l'application ; absent : non. */
  openSlots?: { enabled: boolean };
}

export const instructorApi = {
  async getAccompaniments(from: string, to: string): Promise<{id:string;candidate_name:string;center:string;timezone:string;starts_at:string;ends_at:string}[]> {
    return (await http.get('/v1/exams/accompaniments/me', { params: {from,to} })).data;
  },
  /**
   * Prochain événement daté (J28) : la leçon la plus proche des prochaines semaines du planning, ou l'accompagnement
   * à l'examen le plus proche, s'il vient avant. Le planning se lit semaine après semaine jusqu'à une leçon ;
   * accompagnements illisibles : la leçon seule.
   */
  async getNextEvent(now = Date.now()): Promise<NextEvent | null> {
    const day = (days: number) => dayjs(now).add(days, 'day').format('YYYY-MM-DD');
    const accompaniments = await instructorApi.getAccompaniments(day(0), day(7 * NEXT_EVENT_WEEKS)).catch(() => []);
    const exam = nextAccompaniment(accompaniments, now);
    for (let week = 0; week < NEXT_EVENT_WEEKS; week += 1) {
      const planning = await instructorApi.getPlanning(day(7 * week));
      const lesson = nextLesson(planning.days.flatMap((d) => d.lessons), now);
      if (lesson) return earliest(lesson, exam);
      if (exam && Date.parse(exam.startsAt) <= Date.parse(planning.weekEnd)) return exam;
    }
    return exam;
  },
  async getToday(): Promise<TodayResponse> {
    const { data } = await http.get<TodayResponse>('/v1/instructor/me/today');
    return data;
  },
  async getPlanning(week?: string): Promise<PlanningResponse> {
    const { data } = await http.get<PlanningResponse>(
      '/v1/instructor/me/planning',
      { params: week ? { week } : undefined },
    );
    return data;
  },
  async getStudents(): Promise<Student[]> {
    const { data } = await http.get<Student[]>('/v1/instructor/me/students');
    return data;
  },
  async searchCandidates(q: string): Promise<CandidateSearchResult[]> {
    const { data } = await http.get<CandidateSearchResult[]>(
      '/v1/instructor/me/candidates-search',
      { params: q ? { q } : undefined },
    );
    return data;
  },
  async addStudent(candidateId: string): Promise<Student> {
    const { data } = await http.post<Student>(
      `/v1/instructor/me/students/${candidateId}`,
    );
    return data;
  },
  async removeStudent(candidateId: string): Promise<void> {
    await http.delete(`/v1/instructor/me/students/${candidateId}`);
  },
  async getExamForecasts(): Promise<ExamForecastsResponse> {
    const { data } = await http.get<ExamForecastsResponse>(
      '/v1/instructor/me/exam-forecasts',
    );
    return data;
  },
  /**
   * Dizaine où l'élève sera prêt : Permio en fait la demande de réservation. `note` : note pour le
   * secrétariat (`null` : pas de note ; une note seule changée ne relance pas Permio).
   */
  async setExamForecast(
    candidateId: string,
    month: string,
    third: 1 | 2 | 3,
    note: string | null,
  ): Promise<ExamForecastsResponse> {
    const { data } = await http.put<ExamForecastsResponse>(
      `/v1/instructor/me/students/${candidateId}/exam-forecast`,
      { month, third, note },
    );
    return data;
  },
  async clearExamForecast(candidateId: string): Promise<ExamForecastsResponse> {
    const { data } = await http.delete<ExamForecastsResponse>(
      `/v1/instructor/me/students/${candidateId}/exam-forecast`,
    );
    return data;
  },
  async getStudentLearningBooklet(id: string): Promise<LearningBooklet> {
    const { data } = await http.get<LearningBooklet>(
      `/v1/instructor/me/students/${id}/learning-booklet`,
    );
    return data;
  },
  async setStudentLearningProgression(
    id: string,
    itemCode: string,
    progression: LearningProgression | null,
  ): Promise<LearningBooklet> {
    const { data } = await http.patch<LearningBooklet>(
      `/v1/instructor/me/students/${id}/learning-booklet/${itemCode}`,
      { progression },
    );
    return data;
  },
  async getStudentStartingEvaluation(id: string): Promise<StartingEvaluation> {
    const { data } = await http.get<StartingEvaluation>(
      `/v1/instructor/me/students/${id}/starting-evaluation`,
    );
    return data;
  },
  async setStudentStartingEvaluation(
    id: string,
    answers: Record<string, string>,
  ): Promise<StartingEvaluation> {
    const { data } = await http.patch<StartingEvaluation>(
      `/v1/instructor/me/students/${id}/starting-evaluation`,
      { answers },
    );
    return data;
  },
  async getStudent(id: string): Promise<StudentDetail> {
    const { data } = await http.get<StudentDetail>(
      `/v1/instructor/me/students/${id}`,
    );
    return data;
  },
  /** Réglages de l'application choisis par l'auto-école (Réglages › App moniteur, J39). */
  async getAppSettings(): Promise<InstructorAppSettings> {
    const { data } = await http.get<InstructorAppSettings>('/v1/instructor/me/app-settings');
    return data;
  },
  async createBilan(payload: CreateBilanPayload): Promise<Bilan> {
    const { data } = await http.post<Bilan>('/v1/instructor/me/bilans', payload);
    return data;
  },
  async cancelLesson(id: string): Promise<InstructorLesson> {
    const { data } = await http.post<InstructorLesson>(
      `/v1/instructor/me/lessons/${id}/cancel`,
    );
    return data;
  },
  async rescheduleLesson(
    id: string,
    payload: { startsAt: string; endsAt: string; notify?: boolean },
  ): Promise<InstructorLesson> {
    const { data } = await http.patch<InstructorLesson>(
      `/v1/instructor/me/lessons/${id}/reschedule`,
      payload,
    );
    return data;
  },
  /** Ouvre un créneau à réserver dans son planning ; `requestId` : une demande répétée n'en ouvre qu'un. */
  async openSlot(payload: { requestId: string; startsAt: string; endsAt: string }): Promise<{ id: string; startsAt: string; endsAt: string }> {
    const { data } = await http.post<{ id: string; startsAt: string; endsAt: string }>('/v1/instructor/me/open-slots', payload);
    return data;
  },
  /** Retire un de ses créneaux encore libres : les élèves ne peuvent plus le réserver. */
  async closeOpenSlot(id: string): Promise<{ id: string; status: 'cancelled' }> {
    const { data } = await http.post<{ id: string; status: 'cancelled' }>(`/v1/instructor/me/open-slots/${id}/close`);
    return data;
  },
  async remindLesson(id: string): Promise<{ ok: boolean }> {
    const { data } = await http.post<{ ok: boolean }>(
      `/v1/instructor/me/lessons/${id}/remind`,
      {},
    );
    return data;
  },
  async getStudentLessons(id: string): Promise<StudentLesson[]> {
    const { data } = await http.get<StudentLesson[]>(
      `/v1/instructor/me/students/${id}/lessons`,
    );
    return data;
  },
  async signAttendance(
    id: string,
    signature: string,
    instructorSignature?: string,
  ): Promise<InstructorLesson> {
    const { data } = await http.post<InstructorLesson>(
      `/v1/instructor/me/lessons/${id}/attendance`,
      { signature, instructorSignature },
    );
    return data;
  },
  /** Absence de l'élève (évaluation de départ, rendez-vous, bilan… : pas d'émargement). */
  async reportAbsence(id: string): Promise<{ id: string; absenceReportedAt: string | null }> {
    const { data } = await http.post<{ id: string; absenceReportedAt: string | null }>(
      `/v1/instructor/me/lessons/${id}/absence`,
      {},
    );
    return data;
  },
  async downloadAttendanceSheet(from?: string, to?: string): Promise<Blob> {
    const response = await http.get('/v1/instructor/me/attendance-sheet', {
      params: { from, to },
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  async getAbsences(): Promise<InstructorAbsence[]> {
    const { data } = await http.get<InstructorAbsence[]>('/v1/instructor/me/absences');
    return data;
  },
  async declareAbsence(payload: {
    requestId: string;
    kind: AbsenceKind;
    startsAt: string;
    endsAt: string;
    note?: string;
  }): Promise<DeclareAbsenceResult> {
    const { data } = await http.post<DeclareAbsenceResult>('/v1/instructor/me/absences', payload);
    return data;
  },
  async cancelAbsence(id: string): Promise<void> {
    await http.delete(`/v1/instructor/me/absences/${id}`);
  },
  async getInvoices(): Promise<MyInvoices> {
    const { data } = await http.get<MyInvoices>('/v1/team/me/invoices');
    return data;
  },
  async sendInvoice(file: File | null, fields: { reference?: string; amount?: string; issuedOn?: string; note?: string }): Promise<InstructorInvoice> {
    const form = new FormData();
    if (file) form.append('file', file);
    for (const [key, value] of Object.entries(fields)) if (value?.trim()) form.append(key, value.trim());
    const { data } = await http.post<InstructorInvoice>('/v1/team/me/invoices', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
  async downloadInvoice(id: string): Promise<Blob> {
    const response = await http.get(`/v1/team/me/invoices/${id}/file`, { responseType: 'blob' });
    return response.data as Blob;
  },
};
